import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { fitToFrame } from '../../utils/fitImage';
import './SmartImage.css';

/**
 * SmartImage — wraps an <img> with two graceful fallbacks:
 *
 * 1. Load failure: when `onError` fires, swap to a typography
 *    placeholder using the supplied `name`.
 *
 * 2. Suspiciously low-res: when the image loads but its natural
 *    dimensions are below `lowResThreshold` (default 400px on the
 *    longer axis), the source is too small to render crisply at
 *    typical product-card sizes — so we swap to the same
 *    typography placeholder rather than show a pixelated photo.
 *
 * The placeholder is intentionally on-brand: cream canvas, serif
 * product name, hairline border. It turns a media defect into an
 * editorial moment, the way Maileg's "coming soon" tiles do, and
 * keeps the visual rhythm intact while the operator sources better
 * photography for these SKUs.
 *
 * Pass `lowResThreshold={null}` (or `0`) to disable the low-res
 * check — useful inside the PDP lightbox where the user has opted
 * in to a closer look at the original asset.
 */
const SmartImage = ({
  src,
  alt,
  name,
  className = '',
  imgClassName = '',
  lowResThreshold = 400,
  onErrorFallback,
  fit = false,
  ...rest
}) => {
  const { t } = useTranslation();
  const [state, setState] = useState('loading');

  const handleLoad = useCallback((e) => {
    const target = e.target;
    if (!target) return;
    if (lowResThreshold && (target.naturalWidth < lowResThreshold && target.naturalHeight < lowResThreshold)) {
      setState('low-res');
    } else {
      setState('ok');
      if (fit) fitToFrame(target);
    }
  }, [lowResThreshold, fit]);

  const handleError = useCallback((e) => {
    if (onErrorFallback && !e.target.dataset.fallback) {
      e.target.dataset.fallback = '1';
      e.target.src = onErrorFallback;
      return;
    }
    setState('error');
  }, [onErrorFallback]);

  const showFallback = state === 'low-res' || state === 'error';

  return (
    <span className={`smart-image ${className}`} data-state={state}>
      {!showFallback && (
        <img
          src={src}
          alt={alt || name || ''}
          className={`smart-image__img ${imgClassName}`}
          loading="lazy"
          onLoad={handleLoad}
          onError={handleError}
          {...rest}
        />
      )}
      {showFallback && (
        <span
          className="smart-image__fallback"
          role="img"
          aria-label={alt || name || t('imageFallback.alt')}
          title={t('imageFallback.label')}
        >
          <span className="smart-image__fallback-name">{name || alt || ''}</span>
          <span className="smart-image__fallback-tag">{t('imageFallback.label')}</span>
        </span>
      )}
    </span>
  );
};

export default SmartImage;
